// Tiny helpers for the Nearby tab: haversine distance + a free reverse
// geocode (nominatim) so we can show "Brooklyn" instead of "40.6, -73.9".

export interface Coords { lat: number; lng: number }

const EARTH_KM = 6371;
const toRad = (d: number) => (d * Math.PI) / 180;

export function distanceKm(a: Coords, b: Coords): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * EARTH_KM * Math.asin(Math.sqrt(x));
}

export const distanceMi = (a: Coords, b: Coords) => distanceKm(a, b) * 0.621371;

export function formatDistance(mi: number): string {
  if (mi < 0.1) return "right here";
  if (mi < 1) return "<1 mi";
  if (mi < 10) return `${mi.toFixed(1)} mi`;
  return `${Math.round(mi)} mi`;
}

// OpenStreetMap Nominatim — free, no key. Polite use: cache & low volume.
export async function reverseGeocode(c: Coords): Promise<string | null> {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${c.lat}&lon=${c.lng}&zoom=10&addressdetails=1`;
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) return null;
    const data = await res.json();
    const a = data.address || {};
    return (
      a.city ||
      a.town ||
      a.village ||
      a.suburb ||
      a.county ||
      a.state ||
      data.display_name?.split(",")[0] ||
      null
    );
  } catch {
    return null;
  }
}
